import React, { useState } from "react";
import {
  IconScan,
  IconPlus,
  IconSearch,
  IconCheck,
  IconAlertTriangle,
  IconBarcode,
  IconQrcode,
  IconEye,
  IconCircleCheck,
  IconCircleX,
  IconBuildingStore,
  IconHistory
} from "@tabler/icons-react";
import {
  useVerificationSessions,
  useVerificationSessionDetails,
  createVerificationSession,
  recordVerificationScan,
  completeVerificationSession,
  useAssetCategories
} from "../../controllers/assets.controller";
import toast from "react-hot-toast";

export default function AssetVerificationTab() {
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [isNewSessionModalOpen, setIsNewSessionModalOpen] = useState(false);
  const [scanCode, setScanCode] = useState("");
  const [scanLocation, setScanLocation] = useState("");
  const [scanCondition, setScanCondition] = useState("Good");
  const [scanNotes, setScanNotes] = useState("");

  const { data: sessions, error, isLoading, mutate: mutateSessions } = useVerificationSessions();
  const { data: sessionDetails, mutate: mutateDetails } = useVerificationSessionDetails(activeSessionId);
  const { data: categories } = useAssetCategories();

  const [sessionFormData, setSessionFormData] = useState({
    title: `Physical Audit - ${new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" })}`,
    audit_date: new Date().toISOString().split("T")[0],
    auditor_name: "",
    scope_type: "All",
    scope_value: ""
  });

  const handleStartSession = async (e) => {
    e.preventDefault();
    if (!sessionFormData.auditor_name) {
      toast.error("Please enter the auditor name");
      return;
    }

    try {
      const res = await createVerificationSession(sessionFormData);
      toast.success("Verification session created!");
      setIsNewSessionModalOpen(false);
      mutateSessions();
      if (res?.data?.id) {
        setActiveSessionId(res.data.id);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to start audit session");
    }
  };

  const handleScanSubmit = async (e) => {
    e.preventDefault();
    if (!scanCode.trim()) return;

    try {
      const res = await recordVerificationScan(activeSessionId, {
        asset_code: scanCode.trim(),
        scanned_location: scanLocation || sessionDetails?.scope_value || "Audited Location",
        actual_condition: scanCondition,
        notes: scanNotes
      });

      if (res?.data?.is_misplaced) {
        toast((t) => (
          <span className="flex items-center gap-2 text-amber-600 font-semibold text-xs">
            <IconAlertTriangle className="w-4 h-4" />
            MISPLACED ASSET! Expected in {res.data.expected_location}
          </span>
        ));
      } else {
        toast.success(`Verified: ${scanCode.trim()}`);
      }

      setScanCode("");
      setScanNotes("");
      mutateDetails();
      mutateSessions();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Scan failed");
    }
  };

  const handleCompleteAudit = async () => {
    if (!window.confirm("Are you sure you want to finalize this audit session?")) return;

    try {
      await completeVerificationSession(activeSessionId, {
        notes: "Physical count completed and reconciled."
      });
      toast.success("Audit session closed and finalized!");
      mutateDetails();
      mutateSessions();
    } catch (err) {
      toast.error("Failed to complete session");
    }
  };

  const items = sessionDetails?.items || [];
  const verifiedCount = items.filter((i) => i.verification_status === "Verified").length;
  const missingCount = items.filter((i) => i.verification_status === "Missing").length;
  const misplacedCount = items.filter((i) => i.verification_status === "Misplaced").length;

  return (
    <div className="space-y-6">
      {/* Active Session Mode Header */}
      {activeSessionId ? (
        <div className="space-y-6">
          {/* Back & Summary Bar */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <div>
              <button
                onClick={() => setActiveSessionId(null)}
                className="text-xs font-semibold text-restro-green hover:underline flex items-center gap-1 mb-1"
              >
                ← Return to All Audit Sessions
              </button>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <IconScan className="w-6 h-6 text-restro-green" />
                {sessionDetails?.title || "Audit Session"}
              </h2>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Auditor: <span className="font-semibold text-gray-800 dark:text-gray-200">{sessionDetails?.auditor_name}</span> | Scope: {sessionDetails?.scope_type} ({sessionDetails?.scope_value || "Entire Property"})
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex gap-2">
                <div className="px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-xs font-bold text-emerald-700 dark:text-emerald-400 text-center">
                  <div>{verifiedCount}</div>
                  <div className="text-[10px] font-normal">Found</div>
                </div>
                <div className="px-3 py-1.5 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-xs font-bold text-amber-700 dark:text-amber-400 text-center">
                  <div>{misplacedCount}</div>
                  <div className="text-[10px] font-normal">Misplaced</div>
                </div>
                <div className="px-3 py-1.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-xs font-bold text-rose-700 dark:text-rose-400 text-center">
                  <div>{missingCount}</div>
                  <div className="text-[10px] font-normal">Missing</div>
                </div>
              </div>

              {sessionDetails?.status !== "Completed" && (
                <button
                  onClick={handleCompleteAudit}
                  className="px-4 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-sm transition"
                >
                  Finalize Audit
                </button>
              )}
            </div>
          </div>

          {/* Scanner Console */}
          {sessionDetails?.status !== "Completed" && (
            <div className="bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent dark:from-emerald-950/40 p-5 rounded-2xl border border-emerald-500/20">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                <IconBarcode className="w-5 h-5 text-restro-green" />
                Physical Scan Console (Laser Scanner or Manual Tag Entry)
              </h3>
              <form onSubmit={handleScanSubmit} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-4">
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Scan Barcode / QR / Enter Asset Code *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. AST-0004 or scan with gun"
                    value={scanCode}
                    onChange={(e) => setScanCode(e.target.value)}
                    autoFocus
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-emerald-500/50 rounded-xl focus:ring-2 focus:ring-restro-green font-mono font-bold text-gray-900 dark:text-white shadow-sm"
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Room / Scanned Location
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Room 204 or Lobby"
                    value={scanLocation}
                    onChange={(e) => setScanLocation(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Observed Condition
                  </label>
                  <select
                    value={scanCondition}
                    onChange={(e) => setScanCondition(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200 font-medium"
                  >
                    <option value="Good">Good</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Fair">Fair / Worn</option>
                    <option value="Damaged">Damaged</option>
                  </select>
                </div>
                <div className="md:col-span-3">
                  <button
                    type="submit"
                    className="w-full py-2 text-sm font-bold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow transition flex items-center justify-center gap-2"
                  >
                    <IconScan className="w-4 h-4" />
                    Record Scan
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Audit Verification Table */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-gray-600 dark:text-gray-300">
                <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 uppercase text-xs font-semibold">
                  <tr>
                    <th className="px-6 py-4">Asset Code</th>
                    <th className="px-6 py-4">Asset Name</th>
                    <th className="px-6 py-4">Expected Location</th>
                    <th className="px-6 py-4">Actual Location</th>
                    <th className="px-6 py-4">Observed Condition</th>
                    <th className="px-6 py-4 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-750 transition">
                      <td className="px-6 py-4 font-mono text-xs font-bold text-gray-900 dark:text-white">
                        {item.asset_code}
                      </td>
                      <td className="px-6 py-4 font-semibold text-gray-900 dark:text-white">
                        {item.asset_name}
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-500">
                        {item.expected_location || "Store"}
                      </td>
                      <td className="px-6 py-4 text-xs font-medium text-gray-800 dark:text-gray-200">
                        {item.scanned_location || "-"}
                      </td>
                      <td className="px-6 py-4 text-xs">{item.actual_condition || "-"}</td>
                      <td className="px-6 py-4 text-center">
                        {item.verification_status === "Verified" ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                            <IconCircleCheck className="w-3.5 h-3.5" /> Found
                          </span>
                        ) : item.verification_status === "Misplaced" ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                            <IconAlertTriangle className="w-3.5 h-3.5" /> Misplaced
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400 border border-rose-200 dark:border-rose-800">
                            <IconCircleX className="w-3.5 h-3.5" /> Missing
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        /* Sessions List Mode */
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <IconScan className="w-6 h-6 text-restro-green" />
                Physical Asset Verification & Audits
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Conduct periodic QR/barcode physical inventory counts to detect missing or misplaced hotel assets.
              </p>
            </div>
            <button
              onClick={() => setIsNewSessionModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
            >
              <IconPlus className="w-4 h-4" />
              New Verification Session
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-gray-600 dark:text-gray-300">
                <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 uppercase text-xs font-semibold">
                  <tr>
                    <th className="px-6 py-4">Audit Session</th>
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4">Auditor</th>
                    <th className="px-6 py-4">Scope</th>
                    <th className="px-6 py-4">Progress / Findings</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {isLoading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-10 text-gray-400">
                        Loading verification sessions...
                      </td>
                    </tr>
                  ) : !sessions || sessions.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-12 text-gray-400">
                        <IconScan className="w-12 h-12 mx-auto mb-2 opacity-30 text-restro-green" />
                        No verification sessions created yet. Click "New Verification Session" to start.
                      </td>
                    </tr>
                  ) : (
                    sessions.map((s) => (
                      <tr key={s.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-750 transition">
                        <td className="px-6 py-4 font-bold text-gray-900 dark:text-white">
                          {s.title}
                        </td>
                        <td className="px-6 py-4 text-xs">
                          {s.audit_date ? new Date(s.audit_date).toLocaleDateString() : "-"}
                        </td>
                        <td className="px-6 py-4 text-xs font-medium text-gray-800 dark:text-gray-200">
                          {s.auditor_name}
                        </td>
                        <td className="px-6 py-4 text-xs">
                          <span className="font-semibold text-gray-700 dark:text-gray-300">{s.scope_type}</span>
                          {s.scope_value && <div className="text-gray-400">{s.scope_value}</div>}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                            {s.verified_items || 0} / {s.total_items || 0} Assets Verified
                          </div>
                          <div className="w-32 bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full mt-1 overflow-hidden">
                            <div
                              className="bg-restro-green h-full"
                              style={{
                                width: `${
                                  s.total_items > 0 ? ((s.verified_items || 0) / s.total_items) * 100 : 0
                                }%`
                              }}
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                              s.status === "Completed"
                                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
                                : "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400 border border-blue-200 dark:border-blue-800"
                            }`}
                          >
                            {s.status || "In Progress"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => setActiveSessionId(s.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-lg shadow-sm transition"
                          >
                            <IconEye className="w-3.5 h-3.5" />
                            {s.status === "Completed" ? "View Audit Details" : "Enter Scan Console"}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal: New Verification Session */}
      {isNewSessionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg overflow-hidden border border-gray-100 dark:border-gray-700 my-8">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <IconScan className="w-5 h-5 text-restro-green" />
                Initiate Physical Asset Verification
              </h3>
              <button
                onClick={() => setIsNewSessionModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleStartSession} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Session Title *
                </label>
                <input
                  type="text"
                  value={sessionFormData.title}
                  onChange={(e) => setSessionFormData({ ...sessionFormData, title: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Auditor / Lead Name *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Internal Audit Team"
                    value={sessionFormData.auditor_name}
                    onChange={(e) => setSessionFormData({ ...sessionFormData, auditor_name: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Audit Date *
                  </label>
                  <input
                    type="date"
                    value={sessionFormData.audit_date}
                    onChange={(e) => setSessionFormData({ ...sessionFormData, audit_date: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Verification Scope *
                </label>
                <select
                  value={sessionFormData.scope_type}
                  onChange={(e) => setSessionFormData({ ...sessionFormData, scope_type: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200 font-semibold"
                >
                  <option value="All">All Hotel Assets (Complete Physical Count)</option>
                  <option value="Location">Specific Location / Wing / Floor</option>
                  <option value="Category">Specific Category</option>
                  <option value="Department">Specific Department</option>
                </select>
              </div>

              {sessionFormData.scope_type !== "All" && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Scope Filter Target *
                  </label>
                  {sessionFormData.scope_type === "Category" ? (
                    <select
                      value={sessionFormData.scope_value}
                      onChange={(e) => setSessionFormData({ ...sessionFormData, scope_value: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200"
                      required
                    >
                      <option value="">-- Select Category --</option>
                      {categories?.map((c) => (
                        <option key={c.id} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      placeholder={
                        sessionFormData.scope_type === "Location"
                          ? "e.g. Floor 2 or Main Kitchen"
                          : "e.g. Housekeeping"
                      }
                      value={sessionFormData.scope_value}
                      onChange={(e) => setSessionFormData({ ...sessionFormData, scope_value: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200"
                      required
                    />
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsNewSessionModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
                >
                  Create & Populate Scope
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
